import React, { useRef, useEffect, useState } from 'react'
import { MessageSquare, Check, Plus, Volume2, Search, X, Save, Filter } from 'lucide-react'
import type { Transcript, TranscriptSentence, Segment } from '../types'

interface TranscriptPanelProps {
  transcript: Transcript | null
  currentTime: number
  segments: Segment[]
  selectedSentences: string[]
  onSentenceSelect: (sentenceId: string, multiSelect: boolean) => void
  onCreateSegmentFromSentences: (sentenceIds: string[]) => void
  onSeek: (time: number) => void
  isLoading?: boolean
}

// Saved filter presets - stored in localStorage
const FILTER_PRESETS_KEY = 'studio_filter_presets'
const DEFAULT_FILTER_KEY = 'studio_default_filter'

const loadFilterPresets = (): { name: string; words: string }[] => {
  try {
    const saved = localStorage.getItem(FILTER_PRESETS_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

const saveFilterPresets = (presets: { name: string; words: string }[]) => {
  localStorage.setItem(FILTER_PRESETS_KEY, JSON.stringify(presets))
}

const loadDefaultFilter = (): string => {
  return localStorage.getItem(DEFAULT_FILTER_KEY) || ''
}

const saveDefaultFilter = (filter: string) => {
  localStorage.setItem(DEFAULT_FILTER_KEY, filter)
}

export function TranscriptPanel({
  transcript,
  currentTime,
  segments,
  selectedSentences,
  onSentenceSelect,
  onCreateSegmentFromSentences,
  onSeek,
  isLoading = false
}: TranscriptPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLDivElement>(null)
  
  // Filter state
  const [filterWords, setFilterWords] = useState<string>(loadDefaultFilter())
  const [filterPresets, setFilterPresets] = useState<{ name: string; words: string }[]>(loadFilterPresets())
  const [showFilterOptions, setShowFilterOptions] = useState(false)
  const [newPresetName, setNewPresetName] = useState('')
  const [showOnlyMatches, setShowOnlyMatches] = useState(false)

  // Parse filter words into array
  const getFilterWordsArray = (): string[] => {
    return filterWords
      .split(',')
      .map(w => w.trim().toLowerCase())
      .filter(w => w.length > 0)
  }

  // Check if sentence matches filter
  const sentenceMatchesFilter = (sentence: TranscriptSentence): boolean => {
    const words = getFilterWordsArray()
    if (words.length === 0) return true
    const text = sentence.text.toLowerCase()
    return words.some(word => text.includes(word))
  }

  // Get matching words in text for highlighting
  const highlightMatchingWords = (text: string): React.ReactNode => {
    const words = getFilterWordsArray()
    if (words.length === 0) return text
    
    // Create regex to match any filter word
    const regex = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, i) => {
      if (words.some(w => part.toLowerCase() === w.toLowerCase())) {
        return <mark key={i} className="bg-yellow-500 text-black px-0.5 rounded">{part}</mark>
      }
      return part
    })
  }

  // Filter sentences based on current filter
  const filteredSentences = transcript?.sentences.filter(
    sentence => !showOnlyMatches || sentenceMatchesFilter(sentence)
  ) || []

  // Save current filter as preset
  const saveAsPreset = () => {
    if (!newPresetName.trim() || !filterWords.trim()) return
    const newPresets = [...filterPresets, { name: newPresetName.trim(), words: filterWords }]
    setFilterPresets(newPresets)
    saveFilterPresets(newPresets)
    setNewPresetName('')
    setShowFilterOptions(false)
  }

  // Delete preset
  const deletePreset = (index: number) => {
    const newPresets = filterPresets.filter((_, i) => i !== index)
    setFilterPresets(newPresets)
    saveFilterPresets(newPresets)
  }

  // Set as default filter
  const setAsDefault = () => {
    saveDefaultFilter(filterWords)
  }

  // Select all matching sentences
  const selectAllMatching = () => {
    const matchingIds = filteredSentences
      .filter(s => sentenceMatchesFilter(s))
      .map(s => s.id)
    matchingIds.forEach(id => {
      if (!selectedSentences.includes(id)) {
        onSentenceSelect(id, true)
      }
    })
  }

  // Auto-scroll to current sentence
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current
      const active = activeRef.current
      const containerRect = container.getBoundingClientRect()
      const activeRect = active.getBoundingClientRect()
      
      if (activeRect.top < containerRect.top || activeRect.bottom > containerRect.bottom) {
        active.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentTime])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isSentenceActive = (sentence: TranscriptSentence) => {
    return currentTime >= sentence.start && currentTime <= sentence.end
  }

  const isSentenceInSegment = (sentence: TranscriptSentence) => {
    return segments.some(seg => 
      sentence.start >= seg.start && sentence.end <= seg.end
    )
  }

  const getSpeakerColor = (speaker?: string) => {
    if (!speaker) return '#6b7280'
    const colors = [
      '#ef4444', '#3b82f6', '#10b981', '#f59e0b', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
    ]
    const index = speaker.charCodeAt(speaker.length - 1) || 0
    return colors[index % colors.length]
  }

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-gray-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Transcript</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          <span className="ml-3 text-gray-400">Transcribing video...</span>
        </div>
      </div>
    )
  }

  if (!transcript || transcript.sentences.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-gray-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Transcript</h3>
        </div>
        <div className="text-center py-8 text-gray-400">
          <Volume2 className="mx-auto mb-2" size={32} />
          <p>No transcript available</p>
          <p className="text-sm mt-1">Run analysis to generate transcript</p>
        </div>
      </div>
    )
  }

  const matchCount = transcript?.sentences.filter(s => sentenceMatchesFilter(s)).length || 0

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 flex-1 min-h-0 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-600">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="text-gray-400" size={20} />
            <h3 className="text-lg font-semibold text-white">Transcript</h3>
            <span className="text-sm text-gray-500">
              ({showOnlyMatches ? matchCount : transcript.sentences.length} sentences)
            </span>
          </div>
          
          {selectedSentences.length > 0 && (
            <button
              onClick={() => onCreateSegmentFromSentences(selectedSentences)}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition-colors"
            >
              <Plus size={14} />
              Create Clip ({selectedSentences.length})
            </button>
          )}
        </div>

        {/* Filter Input */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={filterWords}
                onChange={(e) => setFilterWords(e.target.value)}
                placeholder="Filter: word1, word2, word3..."
                className="w-full pl-9 pr-8 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
              />
              {filterWords && (
                <button
                  onClick={() => setFilterWords('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilterOptions(!showFilterOptions)}
              className={`p-2 rounded-lg transition-colors ${showFilterOptions ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
              title="Filter options"
            >
              <Filter size={16} />
            </button>
          </div>

          {/* Filter Options Panel */}
          {showFilterOptions && (
            <div className="p-3 bg-gray-700 rounded-lg space-y-3">
              {/* Toggle: Show only matches */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyMatches}
                  onChange={(e) => setShowOnlyMatches(e.target.checked)}
                  className="rounded border-gray-500"
                />
                <span className="text-sm text-gray-300">Show only matching sentences</span>
                {filterWords && (
                  <span className="text-xs text-indigo-400">({matchCount} matches)</span>
                )}
              </label>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <button
                  onClick={selectAllMatching}
                  disabled={matchCount === 0}
                  className="flex-1 text-xs py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded transition-colors"
                >
                  Select All Matches
                </button>
                <button
                  onClick={setAsDefault}
                  disabled={!filterWords}
                  className="flex-1 text-xs py-1.5 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white rounded transition-colors"
                >
                  Set as Default
                </button>
              </div>

              {/* Save Preset */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="Preset name..."
                  className="flex-1 px-2 py-1 text-xs bg-gray-600 border border-gray-500 rounded text-white placeholder-gray-400"
                />
                <button
                  onClick={saveAsPreset}
                  disabled={!newPresetName.trim() || !filterWords}
                  className="px-2 py-1 text-xs bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded flex items-center gap-1 transition-colors"
                >
                  <Save size={12} />
                  Save
                </button>
              </div>

              {/* Saved Presets */}
              {filterPresets.length > 0 && (
                <div className="space-y-1">
                  <span className="text-xs text-gray-400">Saved Presets:</span>
                  <div className="flex flex-wrap gap-1">
                    {filterPresets.map((preset, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 bg-gray-600 rounded text-xs">
                        <button
                          onClick={() => setFilterWords(preset.words)}
                          className="text-gray-200 hover:text-white"
                        >
                          {preset.name}
                        </button>
                        <button
                          onClick={() => deletePreset(i)}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transcript Content */}
      <div 
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2"
      >
        {filteredSentences.map((sentence) => {
          const isActive = isSentenceActive(sentence)
          const isSelected = selectedSentences.includes(sentence.id)
          const inSegment = isSentenceInSegment(sentence)
          const matchesFilter = sentenceMatchesFilter(sentence)
          
          return (
            <div
              key={sentence.id}
              ref={isActive ? activeRef : null}
              className={`
                p-3 rounded-lg cursor-pointer transition-all border
                ${isActive 
                  ? 'bg-indigo-900 bg-opacity-50 border-indigo-500 ring-2 ring-indigo-500 ring-opacity-50' 
                  : isSelected
                    ? 'bg-blue-900 bg-opacity-30 border-blue-500'
                    : matchesFilter && filterWords
                      ? 'bg-yellow-900 bg-opacity-20 border-yellow-600'
                      : inSegment
                        ? 'bg-green-900 bg-opacity-20 border-green-700'
                        : 'bg-gray-700 bg-opacity-30 border-gray-700 hover:border-gray-500'
                }
              `}
              onClick={(e) => onSentenceSelect(sentence.id, e.ctrlKey || e.metaKey || e.shiftKey)}
            >
              <div className="flex items-start gap-3">
                {/* Selection Checkbox */}
                <div className={`
                  w-5 h-5 rounded border flex items-center justify-center mt-0.5 flex-shrink-0
                  ${isSelected 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'border-gray-500 hover:border-gray-400'
                  }
                `}>
                  {isSelected && <Check size={12} className="text-white" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {/* Timestamp */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onSeek(sentence.start)
                      }}
                      className="text-xs font-mono text-indigo-400 hover:text-indigo-300"
                    >
                      {formatTime(sentence.start)}
                    </button>
                    
                    {/* Speaker Badge */}
                    {sentence.speaker && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: getSpeakerColor(sentence.speaker) }}
                      >
                        {sentence.speaker}
                      </span>
                    )}
                    
                    {/* In Segment Badge */}
                    {inSegment && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-700 text-green-200">
                        In Segment
                      </span>
                    )}
                  </div>
                  
                  {/* Sentence Text */}
                  <p className={`
                    text-sm leading-relaxed
                    ${isActive ? 'text-white font-medium' : 'text-gray-300'}
                  `}>
                    {filterWords ? highlightMatchingWords(sentence.text) : sentence.text}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer with Selection Info */}
      {selectedSentences.length > 0 && (
        <div className="p-3 border-t border-gray-600 bg-gray-750">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">
              {selectedSentences.length} sentence{selectedSentences.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => selectedSentences.forEach(id => onSentenceSelect(id, true))}
              className="text-gray-400 hover:text-white"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
